import React, { useState, useEffect } from "react";
import { Check, MapPin, CheckCircle2 } from "lucide-react";
import { NATIONAL } from "../config/locations";
import { StarRating, SectionEyebrow } from "../components/common";
import { useToast } from "../hooks/use-toast";
import { trackPixel } from "../lib/pixel";

const NationalLanding = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", zip: "" });
  const [smsConsent, setSmsConsent] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Clinic Book — Find Your Nearest MedSpa";
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.zip) {
      toast({ title: "Please complete all fields", description: "We need your name, phone, email and zip code to find your nearest location." });
      return;
    }
    if (!smsConsent) {
      toast({ title: "Please agree to receive text messages", description: "We need your consent to send appointment reminders and confirmations." });
      return;
    }
    // MOCK: save the nationwide lead locally for the demo
    const lead = { ...form, at: new Date().toISOString() };
    const prev = JSON.parse(localStorage.getItem("national_leads") || "[]");
    localStorage.setItem("national_leads", JSON.stringify([lead, ...prev]));
    trackPixel("Lead", { content_name: "Nationwide Booking Request" });
    setDone(true);
    toast({ title: "Got it!", description: "We'll reach out to confirm your nearest location." });
  };

  const inputCls =
    "w-full rounded-xl border border-brand-cream bg-brand-ivory px-4 py-3.5 font-sans text-sm text-brand-ink outline-none transition focus:border-brand-sage focus:ring-2 focus:ring-brand-sage/20";

  return (
    <div className="min-h-screen bg-brand-ivory">
      {/* slim top bar */}
      <div className="w-full bg-brand-sage py-2.5 text-center">
        <p className="font-sans text-[13px] font-medium tracking-wide text-brand-ivory">
          Now booking at locations nationwide
        </p>
      </div>

      <section className="relative overflow-hidden">
        <div className="mx-auto grid min-h-[calc(100vh-44px)] max-w-7xl grid-cols-1 items-center gap-12 px-5 py-14 md:px-8 lg:grid-cols-2 lg:gap-16">
          {/* Left: copy */}
          <div className="animate-fade-up">
            <SectionEyebrow>{NATIONAL.eyebrow}</SectionEyebrow>
            <h1 className="mt-6 font-serif text-[46px] font-semibold leading-[1.03] text-brand-ink md:text-7xl">
              {NATIONAL.title}
            </h1>
            <p className="mt-6 max-w-md font-display text-xl italic leading-relaxed text-brand-muted">
              {NATIONAL.subtitle}
            </p>
            <ul className="mt-8 space-y-3">
              {NATIONAL.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-sage">
                    <Check size={13} className="text-white" />
                  </span>
                  <span className="font-sans text-[15px] text-brand-ink">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-2">
              <StarRating size={16} />
              <span className="font-sans text-sm text-brand-muted">Loved by thousands of clients nationwide</span>
            </div>
          </div>

          {/* Right: image + form */}
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="overflow-hidden rounded-3xl shadow-[0_40px_90px_-40px_rgba(35,35,32,0.55)]">
              <img
                src={NATIONAL.image}
                alt="Aesthetic treatment"
                className="h-56 w-full object-cover md:h-72"
              />
            </div>

            <div className="mt-6 rounded-3xl bg-white p-6 shadow-[0_30px_80px_-30px_rgba(35,35,32,0.35)] ring-1 ring-brand-cream md:p-8">
              {done ? (
                <div className="flex flex-col items-center py-8 text-center animate-fade-in">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-sage/10">
                    <CheckCircle2 className="text-brand-sage" size={30} />
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-brand-ink">Thanks, {form.name.split(" ")[0]}!</h3>
                  <p className="mt-2 max-w-sm font-sans text-sm text-brand-muted">
                    We've received your details. Our team will reach out shortly to confirm your nearest
                    location and get your treatment booked in.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <h3 className="font-serif text-2xl text-brand-ink">Find your nearest location</h3>
                  <p className="mt-1 font-sans text-sm text-brand-muted">
                    Enter your details and we'll be in touch to confirm.
                  </p>
                  <div className="mt-5 space-y-3">
                    <input className={inputCls} placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input className={inputCls} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    <input className={inputCls} type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <div className="relative">
                      <MapPin size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
                      <input className={`${inputCls} pl-10`} placeholder="Zip Code" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
                    </div>
                  </div>
                  <label className="mt-4 flex items-start gap-2.5 text-left">
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      data-testid="sms-consent-checkbox"
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-brand-cream accent-brand-sage focus:ring-brand-sage/30"
                    />
                    <span className="font-sans text-[11px] leading-relaxed text-brand-muted">
                      I agree to receive appointment reminder &amp; confirmation text messages from Clinic Book at the number provided. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-ink">Privacy Policy</a> &amp;{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-ink">Terms</a>.
                    </span>
                  </label>
                  <button
                    type="submit"
                    className="mt-6 w-full rounded-full bg-brand-sage px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-brand-ivory shadow-[0_10px_30px_-8px_rgba(46,69,57,0.6)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sagedark"
                  >
                    Find My Nearest Location
                  </button>
                  <p className="mt-3 text-center font-sans text-xs text-brand-muted">
                    We'll reach out to confirm your nearest location. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NationalLanding;
